import { ActorTree } from '../types/actor-types';
import { SiteNode } from '../types/types';

export class ActorTreeTransformer {
  /**
   * Transform ActorTree data into hierarchical SiteNode format for the UsageTable
   * @param actors - Array of ActorTree data
   * @param selectedActorId - Optional actor ID to use as root (shows only this actor and its children)
   */
  static transformToSiteNodes(actors: ActorTree[], selectedActorId?: string): SiteNode[] {
    if (!actors || actors.length === 0) {
      return [];
    }

    // If a specific actor is selected, show the full hierarchy path
    if (selectedActorId) {
      const selectedId = parseInt(selectedActorId);
      const selectedActor = actors.find(actor => actor.actorId === selectedId);
      
      if (!selectedActor) {
        return [];
      }

      // Get the full path from root to selected actor (ancestors)
      const getAncestors = (actorId: number): ActorTree[] => {
        const actor = actors.find(a => a.actorId === actorId);
        if (!actor) return [];
        
        if (actor.parentActorId) {
          const parent = actors.find(a => a.actorId === actor.parentActorId);
          if (parent) {
            return [...getAncestors(actor.parentActorId), actor];
          }
        }
        return [actor];
      };

      // Get all descendants of the selected actor
      const getDescendants = (parentId: number): ActorTree[] => {
        const children = actors.filter(actor => actor.parentActorId === parentId);
        const allDescendants = [...children];
        
        children.forEach(child => {
          allDescendants.push(...getDescendants(child.actorId));
        });
        
        return allDescendants;
      };

      // Get the complete hierarchy: ancestors + selected + descendants
      const fullPath = getAncestors(selectedId);
      const descendants = getDescendants(selectedId);
      const allRelevantActors = [...fullPath, ...descendants];
      
      // Remove duplicates
      const uniqueActors = allRelevantActors.filter((actor, index, self) => 
        index === self.findIndex(a => a.actorId === actor.actorId)
      );
      
      // Find the root of this hierarchy
      const rootActor = fullPath[0];
      
      // Transform with the root actor, but the hierarchy will naturally show the path
      return [this.transformActor(rootActor, uniqueActors, new Set(), selectedActorId)];
    }

    // Create a map for quick lookup
    const actorMap = new Map<number, ActorTree>();
    actors.forEach(actor => actorMap.set(actor.actorId, actor));

    // Build hierarchy
    const rootNodes: SiteNode[] = [];
    const processedIds = new Set<number>();

    // Find root actors (those without parents or whose parents don't exist in the data)
    actors.forEach(actor => {
      const isRoot = !actor.parentActorId || !actorMap.has(actor.parentActorId);
      if (isRoot && !processedIds.has(actor.actorId)) {
        processedIds.add(actor.actorId);
        rootNodes.push(this.transformActor(actor, actors, processedIds, selectedActorId));
      }
    });

    return rootNodes;
  }

  private static transformActor(actor: ActorTree, allActors: ActorTree[], processedIds: Set<number>, selectedActorId?: string): SiteNode {
    const children: SiteNode[] = [];
    
    // Find all child actors
    const childActors = allActors.filter(a => a.parentActorId === actor.actorId);
    childActors.forEach(childActor => {
      if (!processedIds.has(childActor.actorId)) {
        processedIds.add(childActor.actorId);
        children.push(this.transformActor(childActor, allActors, processedIds, selectedActorId));
      }
    });

    const isSelected = selectedActorId === actor.actorId.toString();

    return {
      name: actor.name || `Actor ${actor.actorId}`,
      id: actor.actorId.toString(),
      type: 'actor',
      status: this.getStatusFromUsage(actor.machineUsage),
      sessions: actor.machinesCount ? `${actor.machinesCount} machines` : '-',
      machinesInSession: actor.machinesCount || 0,
      totalMachines: actor.machinesCount || 0,
      offered: '-',
      actual: '-',
      allocated: {
        current: 0,
        total: actor.machinesCount || 0,
        percentage: 0
      },
      highlighted: isSelected, // Highlight the selected actor
      children: children.length > 0 ? children : undefined
    };
  }

  private static getStatusFromUsage(usage?: string): string {
    switch (usage) {
      case 'Public':
        return 'Public';
      case 'SemiPublic':
        return 'Semi-Public';
      case 'PrivateHouse':
        return 'Private';
      case 'SharedBuilding':
        return 'Shared';
      default:
        return 'Unknown';
    }
  }
}